'use client';

import React from 'react';
import Image from 'next/image';

interface TeamMember {
    name: string;
    role: string;
    description: string;
    imageUrl: string;
    isFounder?: boolean;
}

const teamMembers: TeamMember[] = [
    {
        name: "Sandy King",
        role: "Founder",
        description: "Sandy launched WiderTalk in 2024, driven by growing frustration with the limited commentary options available. Originally educated in Aerospace Engineering, he pivoted to software development and has accumulated diverse experience across Finance, Energy, and Electric Vehicle Systems. A passionate cyclist, Sandy has more recently embraced swimming and pilates.",
        imageUrl: "https://widertalkstack-profilepicturebucket13af4749-e94bwazlhgzc.s3.eu-west-1.amazonaws.com/sandy_profile.jpeg",
        isFounder: true
    },
    {
        name: "Patricia Bertea",
        role: "Marketing Strategy Advisor",
        description: "Patricia is an academic & entrepreneur who brings huge expertise in research and marketing strategy to WiderTalk. With a PhD in E-commerce and Consumer Behaviour, she provides advanced marketing research expertise. Patricia launched her own startup Poloniq in 2018, a SaaS platform for restaurant customer relationship management, and is actively involved in the startup community organizing entrepreneurship events for students.",
        imageUrl: "https://widertalkstack-profilepicturebucket13af4749-e94bwazlhgzc.s3.eu-west-1.amazonaws.com/patricia_profile_2.jpeg"
    },
    {
        name: "Ben Ramsden",
        role: "Marketing and Business Development Manager",
        description: "Ben is the Marketing and Business Development Manager at WiderTalk. He is also an Assistant Sports Director for the Loughborough Lightning Cycling team, and is studying Product Design Engineering. His combination of innovative design skills, passion for sport, and enthusiasm for business growth makes him a valuable member of the WiderTalk team.",
        imageUrl: "https://widertalkstack-profilepicturebucket13af4749-e94bwazlhgzc.s3.eu-west-1.amazonaws.com/ben_profile.jpeg"
    },
    {
        name: "Andrzej Szeszo",
        role: "SRE & DevOps Expert",
        description: "Andrzej is WiderTalk's SRE & Devops Expert. He has wealth of experience working with AWS to build and support high load, scalable solutions. We have yet to see an infrastructure problem Andrzej cannot solve!",
        imageUrl: "https://widertalkstack-profilepicturebucket13af4749-e94bwazlhgzc.s3.eu-west-1.amazonaws.com/andrzej_profile.jpeg"
    },
    {
        name: "Jan Hingar",
        role: "Start-up Coach & Strategic Advisor",
        description: "Jan Hingar is a start-up coach, sports enthusiast, and mental health advocate with a background at Boston Consulting Group (BCG). With a passion for empowering early-stage ventures, Jan blends strategic expertise with a deep commitment to wellbeing and high-performance culture in sport and business alike.",
        imageUrl: "https://widertalkstack-profilepicturebucket13af4749-e94bwazlhgzc.s3.eu-west-1.amazonaws.com/jan_profle.jpeg"
    },
    {
        name: "Armand Bertea",
        role: "Technical Advisor",
        description: "Armand Bertea is a Technical Advisor for WiderTalk and the founder of Football Brain (FBIN). With a strong background in technology and sports analytics, Armand brings expertise and innovative insights to enhance user experience and drive strategic growth. As an owner of FBIN, he is passionate about leveraging data to transform how football insights are shared and understood.",
        imageUrl: "https://widertalkstack-profilepicturebucket13af4749-e94bwazlhgzc.s3.eu-west-1.amazonaws.com/armand_profile.jpeg"
    },
];

export default function TeamPage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--cream)' }}>
      {/* Hero Section */}
      <div className="bg-gradient-to-br text-white py-20" style={{
        background: 'linear-gradient(to bottom right, var(--navy-blue), var(--lightSecondary))'
      }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 gendy-font text-white">
            Meet Our Team
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed" style={{ color: 'var(--cream)' }}>
            The passionate individuals behind WiderTalk, working together to revolutionize sports commentary
          </p>
        </div>
      </div>

      {/* Team Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {teamMembers.map((member, index) => (
            <div
              key={member.name}
              className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group ${
                member.isFounder ? 'lg:col-span-3 md:col-span-2' : ''
              }`}
            >
              <div className={`${member.isFounder ? 'md:flex md:items-center' : ''}`}>
                {/* Profile Image */}
                <div className={`relative ${
                  member.isFounder 
                    ? 'md:w-1/3 h-80 md:h-96' 
                    : 'h-80'
                } overflow-hidden`}>
                  <Image
                    src={member.imageUrl}
                    alt={`${member.name} - ${member.role}`}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    sizes={member.isFounder ? "(max-width: 768px) 100vw, 33vw" : "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"}
                  />
                  {member.isFounder && (
                    <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-semibold" style={{
                      backgroundColor: 'var(--primary)',
                      color: 'var(--navy-blue)'
                    }}>
                      Founder
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className={`p-6 ${member.isFounder ? 'md:w-2/3 md:p-8' : ''}`}>
                  <h3 className="text-2xl font-bold mb-2 gendy-font" style={{ color: 'var(--navy-blue)' }}>
                    {member.name}
                  </h3>
                  <p className="text-lg font-semibold mb-4" style={{ color: 'var(--blue)' }}>
                    {member.role}
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    {member.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Join Our Team Section */}
      <div className="py-20" style={{
        background: 'linear-gradient(to right, var(--primary), var(--lime-green))'
      }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 gendy-font" style={{ color: 'var(--navy-blue)' }}>
            Join Our Team
          </h2>
          <p className="text-xl mb-8 leading-relaxed" style={{ color: 'var(--navy-blue)' }}>
            Interested in joining the WiderTalk team? If you are passionate about marketing, sports, or tech and want to shake up the sports commentary market, get in touch!
          </p>
          <button
            onClick={() => window.location.href = '/contact'}
            className="text-white px-8 py-4 rounded-full text-lg font-semibold hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl"
            style={{ backgroundColor: 'var(--navy-blue)' }}
          >
            Get In Touch
          </button>
        </div>
      </div>
    </div>
  );
}
